import React, { useState, useEffect, useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import utils from '@ehi/global-marketing-interface/lib/utils';
import { i18n } from 'utils/i18n';
import { safeExecute } from 'utils/validation';
import { scrollLock } from 'utils/scrollLock';
import { analyticsInteraction } from 'utils/analytics';
import { isReservationFlow } from 'utils/reservation';
import { setAriaHiddenByElementAttribute } from 'utils/accessibility/aria';
import { findTabbableDescendants } from 'utils/accessibility/tabbable';
import { formatKeys } from 'utils/accessibility/keys';
import { onFocusScrollToTop, loadingOverlay } from 'utils/dom';
import { LOCATIONS, IDS, ANALYTICS } from 'constants';
import cn from 'classnames';
import useClickOutside from 'hooks/useClickOutside';
import FieldControl from 'components/Form/FieldControl';
import Button from 'components/Button';
import LocationSuggestion from './LocationSuggestion';

const LOCATION_TYPE_LABELS = {
  airports: i18n('location_search_suggestions_airports'),
  portsOfCall: i18n('location_search_suggestions_portsOfCall'),
  railStations: i18n('location_search_suggestions_railStations'),
  cities: i18n('location_search_suggestions_cities'),
  branches: i18n('location_search_suggestions_branches'),
};
const isIosSafari = () =>
  /iPhone/.test(navigator.userAgent) && /Mobile/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent);
/**
 * LocationSearch - Location search component
 * @extends Component
 */

const LocationSearch = forwardRef(
  (
    {
      id,
      name,
      label,
      locationsTextQueryResults = {},
      breakpoint,
      selectedLocation,
      toggleOneWay,
      isOneWay,
      recentSearchItems,
      clearRecentSearches,
      isFetching,
      showGuidedError,
      renderGuidedError,
      fetchRecentSearches,
      input,
      selectLocation,
      selectOnBlur,
      locationsTextQuery,
      clearTextQueryLocations,
      onSelectLocation,
      unsetLocation,
      type,
      redirectToLocationFinder,
      formId,
      differentReturnFocus,
      ariaControls = 'returnLocation',
      form,
      isProgressBarExpanded,
    },
    // eslint-disable-next-line no-unused-vars
    _ref
  ) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [error, setError] = useState(null);
    const [geoId, setGeoId] = useState(null);
    const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
    const [isDragging, setIsDragging] = useState(false);
    const [handleFocus, setHandleFocus] = useState(true);

    const inputRef = useRef(null);
    const prevSelectLocation = useRef(selectLocation);
    const prevActiveRef = useRef(null);
    const clickOutsideRef = useRef(null);
    const differentToggleButtonRef = useRef(null);

    // useffect to handle keyboard focus remains  on location field when location is changed or updated,
    // only if active form field is undefined or null to avoid interupting guided form.
    useEffect(() => {
      const formState = form?.getState();
      if (formState?.active) {
        prevActiveRef.current = formState.active;
        // if location is null, this conidition fires when user manually clear selected location on input field
        if (!selectedLocation && prevActiveRef.current === 'pickupLocation' && formState.active === 'pickupLocation') {
          inputRef.current?.focus?.();
        }
      }
      // This condition is for handling keyboard stays on input field, comparing current active form state and prev active state
      if (!!formState && typeof formState?.active === 'undefined' && prevActiveRef.current === 'pickupLocation') {
        const element = document.querySelector('.locationSearch__selectedLocation');

        if (element) {
          element.focus();
        }
        if (!selectedLocation) {
          inputRef.current.focus();
        }
      }
    }, [selectedLocation]);

    // ensuring keyboard focus remains on selected location button when progess bar is expanded
    useEffect(() => {
      if (isProgressBarExpanded) {
        const selectedLocationElement = document.querySelector('.locationSearch__selectedLocation');
        if (selectedLocationElement) {
          selectedLocationElement.focus();
        }
      }
    }, [isProgressBarExpanded]);

    useEffect(() => {
      if (differentReturnFocus) {
        differentToggleButtonRef.current?.focus();
      }
    }, [differentReturnFocus, isOneWay]);
    /**
     * fetches recent search history from local storage
     *
     */
    useEffect(() => {
      safeExecute(fetchRecentSearches);
      window.addEventListener('touchmove', () => setIsDragging(true));
      window.addEventListener('touchstart', () => setIsDragging(false));

      // Safari uses [back-forward cache] that shows the cached page when the user clicks the back button
      // so, we need to hide the loading overlay when the page is loaded from the cache
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          loadingOverlay(false);
        }
      });

      return () => {
        window.removeEventListener('touchmove', () => setIsDragging(true));
        window.removeEventListener('touchstart', () => setIsDragging(false));
      };
    }, []);

    useEffect(() => {
      if (prevSelectLocation.current && !selectLocation) {
        input.onChange('');
      }
      if (!prevSelectLocation.current && selectLocation) {
        input.onChange(selectLocation);
      }
      prevSelectLocation.current = selectLocation;
    }, [selectLocation]);

    const moveOverlay = () => {
      document.getElementById('dummyInput').focus();
      setTimeout(() => {
        document.getElementById(id).focus();
      }, 0);
    };

    /**
     * onLocationSelect - Handles location select - depending on this component type either redirects user to appropriate destination or passes location object to container
     *
     * @param {Object} location Location object from Solr
     */
    const onLocationSelect = (location) => {
      setIsActive(false);
      setSearchTerm('');
      setGeoId(null);
      setError(null);
      setHandleFocus(false);
      // input.onChange(location);
      // Trigger onSelectLocation method provided by the wrapper with component type specific handling
      onSelectLocation(location, input);
      clearTextQueryLocations();
      input.onBlur();
      prevActiveRef.current = 'pickupLocation';
      // Analytics
      analyticsInteraction(
        ANALYTICS.RESERVATION,
        id === IDS.inputPickupLocation ? ANALYTICS.PICKUP : ANALYTICS.RETURN,
        location.name,
        {
          numberOfResults: Object.values(locationsTextQueryResults).flat(1).length || recentSearchItems?.length,
          searchTerm,
        }
      );
    };

    const autoSelectLocation = () => {
      const airportMatch = locationsTextQueryResults?.airports?.find(
        ({ airport_code }) => airport_code.toLowerCase() === searchTerm.toLowerCase()
      );
      if (airportMatch) {
        onLocationSelect(airportMatch);
      } else {
        const city = locationsTextQueryResults?.cities?.[0];
        city && onLocationSelect(city);
      }
    };

    const handleClickOutside = () => {
      // do not process scroll lock toggle to avoid scrolling to top when not intended
      if (!isActive) {
        return;
      }

      setIsActive(false);
      scrollLock({ toggleValue: false });
      input.onBlur();
      if (searchTerm.length > 2 && selectOnBlur) {
        autoSelectLocation();
      }
    };

    useClickOutside(
      {
        container: clickOutsideRef,
        handler: handleClickOutside,
        listenerConditional: isActive,
      },
      [isActive]
    );

    /**
     * handleOnFocus - Activates the component
     */
    const handleOnFocus = (event) => {
      event.preventDefault();
      if (handleFocus) {
        setIsActive(true);
        if (breakpoint.isTabletOrMobile) {
          if (isIosSafari()) {
            setHandleFocus(false);
            setTimeout(() => {
              moveOverlay();
            }, 0);
          } else {
            onFocusScrollToTop(formId, breakpoint.isMobile);
            inputRef.current.focus();
          }
        }
        input.onFocus();
      } else {
        setHandleFocus(true);
      }
      if (selectLocation && isActive) {
        setIsActive(false);
      }
    };

    const handleOnBlur = (e) => {
      if (handleFocus) {
        // IE11 does not support relatedTarget on blur(correctly), therefore:
        const relatedTarget =
          e.nativeEvent.relatedTarget || document.activeElement === document.getElementById(id)
            ? document.activeElement
            : null;

        if (!breakpoint.isTabletOrMobile && !relatedTarget) {
          setIsActive(false);
          scrollLock({ toggleValue: false });
          input.onBlur();
        }
      }
    };

    /**
     * performSearch - Dispatches debounced location search action
     *
     * @param {String} searchTerm Location search query
     */
    const performSearch = utils.debounce((searchTermQuery) => {
      safeExecute(locationsTextQuery, searchTermQuery);
    }, 250);

    /**
     * handleOnChange - Initiates search and terminates geo location request
     *
     * @param {Event} e DOM event
     */
    const handleOnChange = (e) => {
      const newSearchTerm = e.target.value;
      if (geoId) {
        navigator.geolocation.clearWatch(geoId);
      }
      setError(null);
      setGeoId(null);
      setSearchTerm(newSearchTerm);
      performSearch(newSearchTerm);
      if (newSearchTerm.length < 3) {
        clearTextQueryLocations();
      }
    };

    /**
     * handleLocationSuccess - Dispatches action that saves user location information in session and redirects user to storefinder
     *
     * @param {Object} position User location information
     */
    const handleLocationSuccess = (position) => {
      const { latitude, longitude } = position.coords;
      const location = {
        name: i18n('location_search_user_location_label'),
        gps: { latitude, longitude },
        type: LOCATIONS.TYPE_GEOLOCATION,
      };

      navigator.geolocation.clearWatch(geoId);
      setGeoId(null);
      if (type === LOCATIONS.LOCATION_SEARCH_TYPES.STANDALONE) {
        redirectToLocationFinder(location);
      } else {
        onLocationSelect(location); // onLocationSelect
      }
    };

    /**
     * handleLocationError - Geo location error handler - notifies user of a failed location detection attempt
     */
    const handleLocationError = () => {
      navigator.geolocation.clearWatch(geoId);
      setIsActive(true);
      setGeoId(null);
      setError(i18n('location_search_locationError'));
      input.onFocus();
    };

    /**
     * handleLocationButton - Initiate user geo location search
     */
    const handleLocationButton = () => {
      const newGeoId = navigator.geolocation.getCurrentPosition(handleLocationSuccess, handleLocationError, {
        enableHighAccuracy: false,
        maximumAge: 30000,
        timeout: 27000,
      });
      setSearchTerm('');
      setGeoId(newGeoId);

      // Analytics
      analyticsInteraction(
        ANALYTICS.RESERVATION,
        id === IDS.inputPickupLocation ? ANALYTICS.PICKUP : ANALYTICS.RETURN,
        ANALYTICS.USE_CURRENT_LOCATION
      );
    };

    /**
     * handleCloseCta - Deactivates fullscreen view in mobile breakpoint
     *
     * @returns {type} Description
     */
    const handleCloseCta = () => {
      if (geoId) {
        navigator.geolocation.clearWatch(geoId);
      }
      setIsActive(false);
      setSearchTerm('');
      setGeoId(null);
      setError(null);
      scrollLock({ toggleValue: false });
      input.onBlur();
    };

    /**
     * handleKeyDown - Handles keyboard interaction for accessibility
     *
     * @param {Event} e
     */
    const handleKeyDown = (e) => {
      const key = formatKeys(e);
      if (key.arrowUpOrDown) {
        e.preventDefault();
        const newIndex = key.arrowUp ? focusedItemIndex - 1 : focusedItemIndex + 1;
        if (
          (newIndex >= 0 && newIndex <= Object.values(locationsTextQueryResults).flat(1).length) ||
          (!!locationsTextQueryResults && newIndex <= Object.values(recentSearchItems).flat(1).length)
        ) {
          setFocusedItemIndex(newIndex);
        }
      } else if (key.escape) {
        setFocusedItemIndex(null);
        setIsActive(false);
        input.onBlur();
      } else if (key.tab) {
        const node = clickOutsideRef.current;
        const focusables = findTabbableDescendants(node);
        const lastFocusable = focusables[focusables.length - 1];
        if (lastFocusable === document.activeElement) {
          setFocusedItemIndex(null);
          setIsActive(false);
          input.onBlur();
        }
      }
    };

    /**
     * resetFocusedState - Returns focus to the input field
     */
    const resetFocusedState = () => {
      setFocusedItemIndex(-1);
      inputRef.current.focus();
      input.onFocus();
    };

    const handleUnsetLocation = () => {
      setFocusedItemIndex(-1);
      setHandleFocus(true);
      unsetLocation();
      input.onChange('');
      setIsActive(true);
      const element = document.querySelector('.locationSearch');
      if (element) {
        element.focus();
      }
      resetFocusedState();
    };

    const sortLocationsKeys = (keys) => {
      const locationSortWeights = {
        airports: 1,
        railStations: 2,
        portsOfCall: 3,
        cities: 4,
      };
      return keys.sort((a, b) => locationSortWeights[a] - locationSortWeights[b]);
    };

    const ariaDescribedAttribute = (isSuggestionsVisible, showGuidedErrorAttr) => {
      if (isSuggestionsVisible && showGuidedErrorAttr) {
        return { 'aria-describedby': 'locationerror' };
      }
      return {};
    };

    const isSuggestionsVisible = isActive; // && !isFetching;

    let renderedItemCount = -1;
    let emptyMessage;

    if (!error && Object.keys(locationsTextQueryResults).length < 1 && searchTerm.length <= 2) {
      emptyMessage = i18n('location_search_startSearchMessage');
    } else if (error !== null) {
      emptyMessage = error;
    } else if (Object.keys(locationsTextQueryResults).length < 1 && searchTerm.length > 2 && !isFetching) {
      emptyMessage = i18n('location_search_emptyMessage');
    }

    const selectedLocationButtonLabel =
      selectedLocation?.type === LOCATIONS.TYPE_GEOLOCATION
        ? i18n('location_search_user_location_label')
        : selectedLocation?.name;

    /**
     * For Accessibility
     * Set aria-hidden for elements behind id='locationSearch' element.
     * aria-hidden = true when id='locationSearch' is selected
     * aria-hidden = false when id='locationSearch' is in its deafult state
     */
    setAriaHiddenByElementAttribute(
      ['div.booking-widget__input-group', 'div.aem-hero', 'div.title__container--suffix'],
      isSuggestionsVisible
    );

    const ariaLabelBaseText = isOneWay ? `${i18n('common_cancel')}` : `${i18n('common_choose')}`;
    return (
      <div ref={clickOutsideRef} className='locationSearch' onKeyDown={handleKeyDown}>
        {toggleOneWay && isOneWay && breakpoint.isMobile && (
          <Button
            plain
            buttonRef={differentToggleButtonRef}
            tabIndex={0}
            type='button'
            onClick={toggleOneWay}
            title={i18n('location_search_oneway_toggle')}
            aria-label={`${ariaLabelBaseText} ${i18n('location_search_oneway_toggle')}`}
            aria-expanded={isOneWay}
            aria-controls={ariaControls}
            data-dtm-track='button|reservation_widget|different_return'
            className={cn('locationSearch__onewayToggle', {
              'locationSearch__onewayToggle--remove': isOneWay,
            })}
          ></Button>
        )}
        <input
          id={id}
          name={name || id}
          type='text'
          aria-label={label}
          ref={inputRef}
          value={searchTerm}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          disabled={selectedLocation !== null}
          role='combobox'
          aria-autocomplete='list'
          aria-expanded={isSuggestionsVisible}
          aria-owns='locationsearch'
          aria-controls='locationsearch'
          aria-required='true'
          required
          aria-invalid={showGuidedError}
          aria-activedescendant={location.id}
          aria-labelledby='label-pickupLocation'
          {...ariaDescribedAttribute(isSuggestionsVisible, showGuidedError)}
        />
        {breakpoint.isTabletOrMobile && isActive && (
          <Button
            plain
            type='button'
            className='locationSearch__closeCta'
            onClick={handleCloseCta}
            aria-label={i18n('location_search_close_cta')}
          >
            {i18n('location_search_close_cta')}
          </Button>
        )}
        {selectedLocation && (
          <Button
            plain
            type='button'
            className='locationSearch__selectedLocation'
            onClick={handleUnsetLocation}
            aria-label={i18n('location_search_remove_selected_location', [selectedLocationButtonLabel])}
            aria-live='polite'
            aria-atomic='true'
            role='status'
          >
            <span>{selectedLocationButtonLabel}</span>
          </Button>
        )}
        {geoId !== null && (
          <span className='locationSearch__fetch__label animated-loader__after'>
            {i18n('location_search_fetch_label')}
          </span>
        )}
        {toggleOneWay && !(isOneWay && breakpoint.isMobile) && !(isSuggestionsVisible && breakpoint.isMobile) && (
          <Button
            tabIndex={0}
            plain
            buttonRef={differentToggleButtonRef}
            type='button'
            onClick={toggleOneWay}
            className={cn('locationSearch__onewayToggle', { 'locationSearch__onewayToggle--remove': isOneWay })}
            data-dtm-track='button|reservation_widget|different_return'
            aria-expanded={isOneWay}
            aria-label={`${ariaLabelBaseText} ${i18n('location_search_oneway_toggle')}`}
            aria-controls={ariaControls}
          >
            <span className='locationSearch__onewayToggle-label'>{i18n('location_search_oneway_toggle')}</span>
          </Button>
        )}
        {isSuggestionsVisible && (
          <div className='locationSearch__results component-theme--light' role='listbox'>
            {showGuidedError && (
              <p role='alert' id='locationerror' className='locationSearch__results__error-message'>
                {renderGuidedError()}
              </p>
            )}
            {emptyMessage && <div className='locationSearch__results__empty-message'>{emptyMessage}</div>}
            <ul id='locationsearch'>
              {emptyMessage && recentSearchItems.length > 0 && (
                <li className='locationSearch__results__recentSearches locationSearch__results__category'>
                  <h6 id='locationsuggestion' className='locationSearch__results__category__label'>
                    {i18n('location_search_suggestions_recent')}
                    {breakpoint.isDesktop && (
                      <Button
                        plain
                        type='button'
                        className='locationSearch__results__recentSearches__link'
                        data-dtm-track='button|res_widget|clear_recent_searches'
                        aria-label={i18n('location_search_suggestions_clear_recent')}
                        onClick={clearRecentSearches}
                      >
                        {i18n('location_search_suggestions_clear_recent')}
                      </Button>
                    )}
                  </h6>
                  <ul
                    className='locationSearch__results__category__items'
                    role='group'
                    aria-labelledby='locationsuggestion'
                  >
                    {recentSearchItems.map((location) => {
                      renderedItemCount += 1;
                      return (
                        <LocationSuggestion
                          key={location.id || location.name}
                          location={location}
                          selectLocation={onLocationSelect}
                          isFocused={focusedItemIndex === renderedItemCount}
                          resetFocusedState={resetFocusedState}
                          input={input}
                        />
                      );
                    })}
                  </ul>
                  {breakpoint.isTabletOrMobile && (
                    <Button
                      plain
                      type='button'
                      className='locationSearch__results__recentSearches__link'
                      data-dtm-track='button|res_widget|clear_recent_searches'
                      aria-label={i18n('location_search_suggestions_clear_recent')}
                      onClick={clearRecentSearches}
                    >
                      {i18n('location_search_suggestions_clear_recent')}
                    </Button>
                  )}
                </li>
              )}
              {!error &&
                sortLocationsKeys(Object.keys(locationsTextQueryResults)).map((locationKey) => (
                  <li
                    className={`locationSearch__results__category locationSearch__results__category--${locationKey}`}
                    key={locationKey}
                  >
                    <h6 className='locationSearch__results__category__label' id={`cat-${locationKey}`}>
                      {LOCATION_TYPE_LABELS[locationKey]}
                    </h6>
                    <ul
                      className='locationSearch__results__category__items'
                      role='group'
                      aria-labelledby={`cat-${locationKey}`}
                    >
                      {locationsTextQueryResults[locationKey].map((location) => {
                        renderedItemCount += 1;
                        return (
                          <LocationSuggestion
                            key={location.id || location.name}
                            location={location}
                            locationKey={locationKey}
                            selectLocation={onLocationSelect}
                            isFocused={focusedItemIndex === renderedItemCount}
                            resetFocusedState={resetFocusedState}
                            isDragging={isDragging}
                            input={input}
                          />
                        );
                      })}
                    </ul>
                  </li>
                ))}
            </ul>
            {'geolocation' in navigator && (
              <div role='presentation'>
                <Button
                  plain
                  type='button'
                  className='locationSearch__results__geolocation__button link'
                  aria-label={i18n('location_search_geolocation_cta')}
                  onClick={handleLocationButton}
                  role='option'
                  aria-selected={geoId ? 'true' : 'false'}
                >
                  {i18n('location_search_geolocation_cta')}
                </Button>
              </div>
            )}
          </div>
        )}
        <div style={{ height: '0', overflow: 'hidden' }}>
          <input tabIndex='-1' id='dummyInput' type='text' aria-hidden='true' />
        </div>
      </div>
    );
  }
);

const isResflow = isReservationFlow();

const LocationSearchField = (props) => (
  <FieldControl
    name={props.name || props.id}
    {...props}
    fill
    type='text'
    className={cn('location-search-control', {
      'location-search-control__true-modify': props.isTrueModify && isResflow,
    })}
  >
    <LocationSearch {...props} />
  </FieldControl>
);

LocationSearch.propTypes = {
  locationsTextQueryResults: PropTypes.object,
  selectedLocation: PropTypes.object,
  locationsTextQuery: PropTypes.func,
  setLocation: PropTypes.func,
  unsetLocation: PropTypes.func,
  redirectToLocationFinder: PropTypes.func,
  resolveBranchURL: PropTypes.func,
  toggleOneWay: PropTypes.func,
  location_search_return_placeholder: PropTypes.string,
  isOneWay: PropTypes.bool,
  type: PropTypes.string,
  clearTextQueryLocations: PropTypes.func.isRequired,
  breakpoint: PropTypes.object,
  selectOnBlur: PropTypes.bool.isRequired,
  isLocationWithNoVehiclesAvailable: PropTypes.bool,
  ariaControls: PropTypes.string,
  differentReturnFocus: PropTypes.bool,
};

LocationSearch.defaultProps = {
  selectedLocation: null,
  type: 'STANDALONE',
};

export default LocationSearchField;
